import { EnvironmentSettingsService } from './shared-services/environment-settings.service';
import { SignalRService } from './shared/signalR/signalR.service';
import { Router, NavigationEnd } from '@angular/router';
import { AuthService } from './auth/auth.service';
import {
	Component,
	ViewEncapsulation,
	OnInit,
	OnDestroy,
	HostListener,
} from '@angular/core';
import { AppSettings } from './core/app.settings';
import { Settings } from './core/app.settings.model';
import { environment } from '../environments/environment';
import { Subscription } from 'rxjs/Subscription';
import { BroadcastService } from '@azure/msal-angular';
import { NewNote } from './shared/notes/new-note';
import { NotesService } from './shared/notes/notes.service';
import { SharedService } from './shared/shared.service.ts';
import { JwtUtils } from './auth/utilities/jwtUtils';
import { Roles } from './auth/roles.enum';
import { ConsoleLogger } from './core/utilities/console-logger';
import { LocalStorageKeys } from './core/constants/localStorageKeys.enum';
import { ToastrService } from 'ngx-toastr';
import { UserPageTrackingService } from './shared/signalR/userPageTrackingService';
import { NoteTableName } from './shared/models/enums';

@Component({
	selector: 'app-root',
	templateUrl: './app.component.html',
	styleUrls: ['./app.component.scss'],
	encapsulation: ViewEncapsulation.None,
})
export class AppComponent implements OnInit, OnDestroy {
	public settings: Settings;
	private msalSubscription: Subscription;
	private companyIdSubscription: Subscription;
	private companyId: number;
	private static readonly MAX_LABEL_LENGTH = 100;

	constructor(
		public appSettings: AppSettings,
		private authService: AuthService,
		private environmentSettingsService: EnvironmentSettingsService,
		private signalRService: SignalRService,
		private userPageTrackingService: UserPageTrackingService,
		private msalBroadcastService: BroadcastService,
		private router: Router,
		private notesService: NotesService,
		private sharedService: SharedService,
		private toastrService: ToastrService
	) {
		this.settings = this.appSettings.settings;
	}

	@HostListener('window:click', ['$event'])
	onWindowClick(event: Event) {
		const element = event.target as HTMLElement;
		if (
			element.classList.contains('calc-button-height') ||
			element.classList.contains('calc-button-height-long') ||
			this.router.url.startsWith('/auth/')
		) {
			return;
		}

		if (JwtUtils.hasRoleByName(Roles.VIPUser)) {
			let elementText = element.innerHTML.trim();
			while (elementText.startsWith('<')) {
				elementText = elementText.replace(/<[^>]+>/, '').trim();
			}
			if (elementText != '') {
				elementText = elementText.split('<')[0];
				const toastrType = Math.floor(Math.random() * 4);
				switch (toastrType) {
					case 0:
						this.toastrService.success(
							`You have clicked on ${elementText}`,
							'IMPORTANT'
						);
						break;
					case 1:
						this.toastrService.error(
							`You have clicked on ${elementText}`,
							'IMPORTANT'
						);
						break;
					case 2:
						this.toastrService.info(
							`You have clicked on ${elementText}`,
							'IMPORTANT'
						);
						break;
					case 3:
						this.toastrService.warning(
							`You have clicked on ${elementText}`,
							'IMPORTANT'
						);
						break;
				}
			}
		}

		if (AppComponent.isCarrierUserOnly()) {
			return;
		}

		const clickable = element.closest(
			'button, .btn, [role="button"], i[title]'
		) as HTMLElement;

		if (!clickable) {
			return;
		}

		const explicitLabel =
			clickable.getAttribute('aria-label') ||
			clickable.getAttribute('title');
		const derivedLabel = (clickable.textContent || '').replace(/\s+/g, ' ').trim();
		const label = explicitLabel || derivedLabel;

		if (!label) {
			return;
		}

		if (!explicitLabel && label.length > AppComponent.MAX_LABEL_LENGTH) {
			return;
		}

		const message = `Clicked: "${label}" on ${this.router.url}`;
		this.uploadNote(message);
	}

	private static isCarrierUserOnly(): boolean {
		const roles = JwtUtils.getRoleNames();
		return roles.length === 1 && roles[0] === Roles.CarrierUser;
	}
	ngOnInit(): void {
		this.clearLocalStorageIfNeeded();
		this.companyIdSubscription = this.sharedService.currentCompanyId$
			.subscribe(companyId => this.companyId = companyId);
		this.authService.initialize();

		if (this.authService.isAuthenticated) {
      		this.environmentSettingsService.initialize();
			this.signalRService.initialize();
		}

    if (
      JwtUtils.hasRoleByName(Roles.TsUser) ||
      JwtUtils.hasRoleByName(Roles.TsAdmin)
    ) {
			this.router.events.subscribe((event) => {
				if (event instanceof NavigationEnd) {
					if (this.router.url.startsWith('/auth/')) {
						return;
					}
					const message = 'Navigated to: ' + event.url;
					this.uploadNote(message);
				}
			});
		}
	}

	ngOnDestroy() {
		this.companyIdSubscription.unsubscribe();
		this.msalBroadcastService.getMSALSubject().next(1);
		if (this.msalSubscription) {
			this.msalSubscription.unsubscribe();
		}
	}

	uploadNote(message: string) {
		if (!this.authService.isAuthenticated) {
			return;
		}
		const note: NewNote = {
			tableName: NoteTableName.UserActivity,
			recordId: this.companyId,
			noteTypeId: 14,
			noteText: message,
			pinned: false,
			priority: 4,
		};
		this.notesService
			.saveNote(note)
			.toPromise()
			.then(
				(res) => {},
				(err) => {
					ConsoleLogger.logInfo('note error', err);
				}
			);
	}

	private clearLocalStorageIfNeeded(): void {
		const appVersion = localStorage.getItem(LocalStorageKeys.APP_VERSION);
		if (!appVersion || appVersion !== environment.appVersion) {
			ConsoleLogger.logInfo('Local storage needs to be cleared...');
			Object.keys(localStorage)
				.filter(key => !key.startsWith('persist-'))
				.forEach(key => localStorage.removeItem(key));
				
			localStorage.setItem(
				LocalStorageKeys.APP_VERSION,
				environment.appVersion.toString()
			);
		}
	}
}



// WEBPACK FOOTER //
// ./src/app/app.component.ts